import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // { id, email, role, name, volunteerId? }
  const [loading, setLoading] = useState(true);

  // ─── Determine role (admin vs student) ───
  const resolveUserData = useCallback(async (authUser) => {
    if (!authUser) return null;

    // Check admin_users first
    const { data: admin } = await supabase
      .from("admin_users")
      .select("name")
      .eq("auth_user_id", authUser.id)
      .maybeSingle();

    if (admin) {
      return {
        id: authUser.id,
        email: authUser.email,
        role: "admin",
        name: admin.name,
      };
    }

    // Otherwise check volunteers
    const { data: volunteer } = await supabase
      .from("volunteers")
      .select("id, name, college_name")
      .eq("auth_user_id", authUser.id)
      .maybeSingle();

    if (volunteer) {
      return {
        id: authUser.id,
        email: authUser.email,
        role: "student",
        name: volunteer.name,
        volunteerId: volunteer.id,
        college: volunteer.college_name,
      };
    }

    // Auth user exists but no profile row yet — treat as student with email as name
    return {
      id: authUser.id,
      email: authUser.email,
      role: "student",
      name: authUser.email.split("@")[0],
    };
  }, []);

  // ─── Restore session on mount + listen for auth changes ───
  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && mounted) {
        const userData = await resolveUserData(session.user);
        setUser(userData);
      }
      if (mounted) setLoading(false);
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user && mounted) {
          const userData = await resolveUserData(session.user);
          setUser(userData);
        } else if (mounted) {
          setUser(null);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [resolveUserData]);

  // ─── Login ───
  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) throw new Error(error.message);

    const userData = await resolveUserData(data.user);
    setUser(userData);

    // If volunteer, insert daily attendance_log (pending)
    if (userData?.role === "student" && userData?.volunteerId) {
      const today = new Date().toISOString().slice(0, 10);
      // Check if already logged today
      const { data: existing } = await supabase
        .from("attendance_log")
        .select("id")
        .eq("volunteer_id", userData.volunteerId)
        .gte("login_time", `${today}T00:00:00`)
        .lte("login_time", `${today}T23:59:59`)
        .limit(1);

      if (!existing || existing.length === 0) {
        await supabase.from("attendance_log").insert({
          volunteer_id: userData.volunteerId,
          status: "pending",
        });
      }
    }

    return userData;
  }, [resolveUserData]);

  // ─── Register (volunteer only) ───
  const register = useCallback(async (name, email, password, college) => {
    const trimmedEmail = email.trim().toLowerCase();

    // 1. Sign up in Supabase Auth
    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
    });

    if (error) throw new Error(error.message);

    // 2. Insert volunteers row
    const { error: volError } = await supabase.from("volunteers").insert({
      auth_user_id: data.user.id,
      name: name.trim(),
      email: trimmedEmail,
      college_name: college.trim(),
    });

    if (volError) {
      // If volunteer insert fails, the auth user still exists — but that's ok,
      // they can re-register/login and we'll handle it
      throw new Error(volError.message);
    }

    return { email: trimmedEmail, name: name.trim(), role: "student" };
  }, []);

  // ─── Reset password ───
  const resetPassword = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: `${window.location.origin}/login?reset=1` }
    );
    if (error) throw new Error(error.message);
  }, []);

  // ─── Logout ───
  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const value = {
    user,
    login,
    register,
    resetPassword,
    logout,
    isAuthenticated: !!user,
    isStudent: user?.role === "student",
    isAdmin: user?.role === "admin",
    loading,
    supabase, // expose for Realtime subscriptions in components
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
